import cx from "classnames";

import LinkButton from "common/components/LinkButton";

import PillButton from "client/common/components/buttons/PillButton";
import { urls } from "client/routes";

import { IClientBrowseCategory } from "../types";
import CategoryContentLookup from "./CategoryContent";

import styles from "../styles/CategoryCard.module.less";

const noop = () => undefined;

// CategoryCard reads only id / slug / name / description from category, so it
// accepts the carousel-display category (and other shapes) without requiring
// the heavier IClientBrowseCategory.product_lines payload.
type CategoryCardCategory = Pick<IClientBrowseCategory, "id" | "slug" | "name" | "description">;

type CategoryCardProps = {
  category: CategoryCardCategory;
  className?: string;
};

const CategoryCardSmall = ({ category, className }: CategoryCardProps) => {
  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;

  return (
    <LinkButton
      className={cx(styles.small, className)}
      key={category.id}
      dest={urls.products.category({ params: { id: category.id } })}
    >
      <div className={styles.name}>{category.name}</div>
      {CategoryContent && (
        <div>
          <CategoryContent.Blob className={styles.blob} />
          <CategoryContent.Icon className={styles.icon} />
        </div>
      )}
    </LinkButton>
  );
};

const CategoryCard = ({ category, className }: CategoryCardProps) => {
  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;

  return (
    <LinkButton
      dest={urls.products.category({ params: { id: category.id } })}
      className={cx(styles.full, className)}
      style={{
        background: `linear-gradient(to top, ${CategoryContent.gradient.start} 50%, ${CategoryContent.gradient.stop} 100%)`,
      }}
    >
      <div className={styles.content}>
        <div className={styles.name}>{category.name}</div>
        <div className={styles.description}>{category.description}</div>
        <PillButton size="xs" variant="secondary" onClick={noop}>
          View All
        </PillButton>
      </div>
      <div>
        <CategoryContent.Blob className={styles.blob} />
        <CategoryContent.Icon className={styles.icon} />
      </div>
    </LinkButton>
  );
};

type TopSellersCardProps = {
  className: string;
};

const TopSellersCard = ({ className }: TopSellersCardProps) => {
  return (
    <div className={cx(styles.full, styles.top, className)}>
      <div className={styles.gradients}>
        <div className={cx(styles.gradient, styles.one)} />
        <div className={cx(styles.gradient, styles.two)} />
        <div className={cx(styles.gradient, styles.three)} />
        <div className={cx(styles.gradient, styles.four)} />
        <div className={cx(styles.gradient, styles.five)} />
      </div>
      <div className={styles.content}>
        <div className={styles.name}>Top Sellers</div>
        <div className={styles.description}>
          Explore our most popular medications on our online store. Ask your veterinarian if
          you&apos;d like to learn more about a specific medication.
        </div>
      </div>
      <div>
        <svg
          width="440"
          height="436"
          viewBox="0 0 440 436"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.blob}
        >
          <path
            d="M2.84073 236.391C-2.72159 194.549 3.55301 150.088 26.3298 114.999C48.6574 80.6015 89.5167 66.6527 125.508 47.4138C162.365 27.7114 197.493 -1.83007 239.281 1.16564C281.491 4.19162 314.529 36.7543 347.895 63.3566C381.551 90.1902 419.911 115.53 433.969 156.388C448.059 197.338 433.791 241.048 423.424 282.877C413.083 324.602 405.612 370.292 373.893 398.794C342.409 427.084 297.077 431.184 254.893 434.313C216.033 437.196 177.089 432.59 141.539 416.03C106.88 399.884 79.9994 372.338 56.4926 341.893C31.9092 310.053 8.14385 276.283 2.84073 236.391Z"
            fill="rgba(255, 255, 255, 0.8)"
          />
        </svg>
        <svg
          viewBox="0 0 233 186"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.icon}
        >
          <path
            d="M109.913 4.09961C112.442 -0.958984 120.03 -0.958984 122.559 4.09961L130.508 19.998L147.852 22.5273C153.633 23.6113 155.801 30.4766 151.827 34.8125L139.18 46.7363L142.071 64.0801C142.793 69.8613 137.012 74.1973 131.954 71.668L116.417 63.3574L100.518 71.668C95.4595 74.5586 89.6782 69.8613 90.4009 64.4414L93.2915 47.0977L80.645 34.8125C76.6704 30.4766 78.8384 23.6113 84.6196 22.5273L101.963 19.998L109.913 4.09961ZM105.938 25.418L85.7036 28.3086C84.6196 28.6699 83.897 29.7539 84.981 30.4766L99.4341 44.9297L96.1821 65.1641C95.8208 66.248 96.9048 66.9707 97.9888 66.6094L116.417 56.8535L134.483 66.6094C135.567 66.9707 136.651 66.248 136.29 65.1641L133.038 44.9297L147.491 30.4766C148.575 29.7539 147.852 28.6699 146.768 28.3086L126.534 25.418L117.5 6.62891H117.139C116.778 5.90625 115.694 5.90625 114.971 6.62891L105.938 25.418ZM81.729 109.969C81.729 100.574 89.3169 92.625 99.0728 92.625H133.76C143.155 92.625 151.104 100.574 151.104 109.969V167.781C151.104 177.537 143.155 185.125 133.76 185.125H99.0728C89.3169 185.125 81.729 177.537 81.729 167.781V109.969ZM87.5103 109.969V167.781C87.5103 174.285 92.5688 179.344 99.0728 179.344H133.76C139.903 179.344 145.323 174.285 145.323 167.781V109.969C145.323 103.826 139.903 98.4062 133.76 98.4062H99.0728C92.5688 98.4062 87.5103 103.826 87.5103 109.969ZM0.791504 133.094C0.791504 123.699 8.37939 115.75 18.1353 115.75H52.8228C62.2173 115.75 70.1665 123.699 70.1665 133.094V167.781C70.1665 177.537 62.2173 185.125 52.8228 185.125H18.1353C8.37939 185.125 0.791504 177.537 0.791504 167.781V133.094ZM6.57275 133.094V167.781C6.57275 174.285 11.6313 179.344 18.1353 179.344H52.8228C58.9653 179.344 64.3853 174.285 64.3853 167.781V133.094C64.3853 126.951 58.9653 121.531 52.8228 121.531H18.1353C11.6313 121.531 6.57275 126.951 6.57275 133.094ZM214.698 138.875C224.092 138.875 232.042 146.824 232.042 156.219V167.781C232.042 177.537 224.092 185.125 214.698 185.125H180.01C170.254 185.125 162.667 177.537 162.667 167.781V156.219C162.667 146.824 170.254 138.875 180.01 138.875H214.698ZM214.698 144.656H180.01C173.506 144.656 168.448 150.076 168.448 156.219V167.781C168.448 174.285 173.506 179.344 180.01 179.344H214.698C220.84 179.344 226.26 174.285 226.26 167.781V156.219C226.26 150.076 220.84 144.656 214.698 144.656Z"
            fill="url(#top-fill)"
          />
          <defs>
            <linearGradient
              id="top-fill"
              x1="-1.57161"
              y1="2.0002"
              x2="401"
              y2="5.03531"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#CCD7B6" />
              <stop offset="0.5" stopColor="#F2E9C8" />
              <stop offset="1" stopColor="#dccce9" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default Object.assign(CategoryCard, {
  Small: CategoryCardSmall,
  TopSellers: TopSellersCard,
});
